import { Injectable } from '@angular/core';
import { ViewportScroller } from '@angular/common';
import { ActivatedRouteSnapshot, Router, Scroll } from '@angular/router';
import { filter } from 'rxjs/operators';

/**
 * The key under which a route declares itself a "scroll group": navigations
 * that stay within the same instance of the group leave the page scrolled
 * where it is, instead of jumping to the top.
 *
 * Declare it in a route's `data` (see `staff-site-routing.module.ts`). The
 * group's identity includes the URL up to and including the declaring route,
 * so moving between two customers' profiles is NOT within one group and does
 * scroll to the top.
 */
export const SCROLL_GROUP_DATA_KEY = 'scrollGroup';

/**
 * Owns scroll behaviour on navigation for the Staff Site. The router is
 * configured with `scrollPositionRestoration: 'disabled'` so this service,
 * not RouterScroller, consumes `Scroll` events; it reproduces the built-in
 * `'enabled'` behaviour plus two additions Angular offers no opt-out for: a
 * navigation within one scroll group (a customer profile's tabs) keeps the
 * scroll position (PRO-1338), and so does a navigation to the same path
 * (PRO-1540).
 */
@Injectable({ providedIn: 'root' })
export class ScrollPositionService {

  // The scroll group of the previous navigation, or null when it was not in
  // one. Compared against the current navigation's group to decide whether the
  // page has really changed.
  private previousScrollGroupKey: string | null = null;

  // The path (no query string or fragment) of the previous navigation, or
  // null before the first one. Compared against the current navigation's path
  // to catch a same-page navigation that isn't within a scroll group.
  private previousPath: string | null = null;

  constructor(
    private router: Router,
    private viewportScroller: ViewportScroller
  ) {}

  /**
   * Begin handling scroll position. Call once, from the root component.
   */
  public init() {
    // RouterScroller does this itself unless 'disabled'; without it the
    // browser's own scroll restoration competes with the positions restored
    // below.
    this.viewportScroller.setHistoryScrollRestoration('manual');

    this.router.events.pipe(
      filter((event): event is Scroll => event instanceof Scroll)
    ).subscribe(event => this.handleScrollEvent(event));
  }

  private handleScrollEvent(event: Scroll) {
    const scrollGroupKey = this.getCurrentScrollGroupKey();
    const previousScrollGroupKey = this.previousScrollGroupKey;
    this.previousScrollGroupKey = scrollGroupKey;

    const path = this.getPath(event.routerEvent.urlAfterRedirects);
    const previousPath = this.previousPath;
    this.previousPath = path;

    // A back/forward navigation carries the position it was left at. Restoring
    // it is what scrollPositionRestoration: 'enabled' did.
    if (event.position) {
      this.viewportScroller.scrollToPosition(event.position);
      return;
    }

    // Moving between the tabs of one customer's profile, or between the two
    // views of its Reservations tab, is not a new page: stay put.
    if (
      (scrollGroupKey !== null) &&
      (scrollGroupKey === previousScrollGroupKey)
    ) {
      return;
    }

    // A query-param-only navigation, e.g. a details modal writing its
    // deep-link parameter, is not a new page (PRO-1540).
    if (path === previousPath) {
      return;
    }

    // Anchors are deliberately not scrolled to: anchorScrolling is left at
    // its default of 'disabled', so a fragment is the page's own business.
    this.viewportScroller.scrollToPosition([0, 0]);
  }

  private getPath(url: string): string {
    return url.split(/[?#]/, 1)[0];
  }

  /**
   * The scroll group the active route belongs to, or null when it
   * is not inside one.
   *
   * The key combines the declared group name with the URL of the route that
   * declared it, so two customers' profiles are different groups even though
   * they share a route config.
   */
  private getCurrentScrollGroupKey(): string | null {
    let route: ActivatedRouteSnapshot | null = this.router.routerState.snapshot.root;
    const pathSegments: string[] = [];

    while (route) {
      pathSegments.push(...route.url.map(urlSegment => urlSegment.path));

      // Read the route's OWN data rather than the snapshot's, which inherits
      // from parent routes and would report every child of a group's root as
      // declaring the group itself.
      const declaredData = route.routeConfig && route.routeConfig.data;
      if (declaredData && declaredData[SCROLL_GROUP_DATA_KEY]) {
        return declaredData[SCROLL_GROUP_DATA_KEY] + ':' + pathSegments.join('/');
      }

      route = route.firstChild;
    }

    return null;
  }
}
